import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { SplitProgressBar } from '@sorare/core/src/components/collections/SplitProgressBar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { ensureHexColor, isLightColor } from '@sorare/core/src/lib/colors';

import { ImprovementChip } from '#src/atoms/ImprovementChip/index.tsx';
import { MissingCardsForCollection } from '#src/components/cart/CollectionUpsell/MissingCardsForCollection/index.tsx';

import type {
  LazyCollectionElement_CardCollectionScoreIncrease,
  LazyCollectionUpsellElementQuery,
  LazyCollectionUpsellElementQueryVariables,
} from './__generated__/index.graphql.ts';

const Container = styled(Vertical)`
  padding: 0 var(--double-unit);
`;

const CollectionProgress = styled(Vertical)`
  padding: var(--intermediate-unit);
  border-radius: var(--unit);
  background-color: var(--background-color);
`;

const FilterShine = styled.span`
  filter: drop-shadow(0px 1px 7px var(--c-yellow-400));
`;

const LAZY_COLLECTION_UPSELL_ELEMENT_QUERY = gql`
  query LazyCollectionUpsellElementQuery($slug: String!, $userSlug: String!) {
    cardCollection(slug: $slug) {
      slug
      anyTeam {
        slug
        styleGuideColor
        ...TeamAvatar_team
      }
      userCardCollection(forUserSlug: $userSlug) {
        id
        ...MissingCardsForCollection_userCardCollection
        ...SplitProgressBar_userCardCollection
      }
      ...SplitProgressBar_cardCollection
      ...MissingCardsForCollection_cardCollection
    }
  }
  ${TeamAvatar.fragments.team}
  ${SplitProgressBar.fragments.cardCollection}
  ${SplitProgressBar.fragments.userCardCollection}
  ${MissingCardsForCollection.fragments.userCardCollection}
  ${MissingCardsForCollection.fragments.cardCollection}
` as TypedDocumentNode<
  LazyCollectionUpsellElementQuery,
  LazyCollectionUpsellElementQueryVariables
>;

type Props = {
  cardCollectionScoreIncrease: LazyCollectionElement_CardCollectionScoreIncrease;
};

export const LazyCollectionElement = ({
  cardCollectionScoreIncrease,
}: Props) => {
  const { currentUser } = useCurrentUserContext();

  const {
    scoreIncrease,
    bonusIncrease,
    cardCollection: { slug },
  } = cardCollectionScoreIncrease;

  const { data } = useQuery(LAZY_COLLECTION_UPSELL_ELEMENT_QUERY, {
    variables: {
      slug,
      userSlug: currentUser?.slug || '',
    },
    skip: !slug || !currentUser?.slug,
  });

  const cardCollection = data?.cardCollection;

  const { styleGuideColor } = cardCollection?.anyTeam || {};
  const backgroundColor =
    styleGuideColor && !isLightColor(ensureHexColor(styleGuideColor))
      ? styleGuideColor
      : 'var(--c-nl-700-opaque)';

  const { userCardCollection } = cardCollection || {};
  const { bonus = 0 } = userCardCollection || {};

  const bonusIncreased = bonusIncrease + bonus;
  const score = userCardCollection?.score || 0;
  const scoreIncreased = scoreIncrease + score;
  return (
    <>
      <Container>
        <CollectionProgress
          style={
            { '--background-color': backgroundColor } as React.CSSProperties
          }
        >
          <Horizontal>
            <TeamAvatar size={32} team={cardCollection?.anyTeam} />
            <ImprovementChip
              Label={({ value }) => (
                <>
                  {value}{' '}
                  <FilterShine>
                    <Lightning />
                  </FilterShine>
                </>
              )}
              value={score}
              valueIncreased={scoreIncreased}
            />
            <ImprovementChip
              Label={v => <>+{v.value * 100}%</>}
              value={bonus}
              valueIncreased={bonusIncreased}
            />
          </Horizontal>
          <SplitProgressBar
            cardCollection={cardCollection}
            userCardCollection={userCardCollection}
            scoreIncrease={scoreIncrease}
            withoutLabel
          />
        </CollectionProgress>
      </Container>
      <MissingCardsForCollection
        userCardCollection={userCardCollection}
        cardCollection={cardCollection}
      />
    </>
  );
};

LazyCollectionElement.fragments = {
  cardCollectionScoreIncrease: gql`
    fragment LazyCollectionElement_CardCollectionScoreIncrease on CardCollectionScoreIncrease {
      id
      scoreIncrease
      bonusIncrease
      cardCollection {
        slug
      }
    }
  ` as TypedDocumentNode<LazyCollectionElement_CardCollectionScoreIncrease>,
};
